import { Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { finalize, Subject, takeUntil } from 'rxjs';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AuxiliarService } from '../../../services/auxiliar.service';
import { PatientService } from '../../../services/patient.service';
import { TranslateService } from '@ngx-translate/core';
import { NGXLogger } from 'ngx-logger';
import _ from 'lodash';
import { extensionValidator } from '../../../utils/validators/extensionValidator';
import { MedicalRecord } from '../../../models/medical-record';
import { MedicalRecordService } from '../../../services/medical-record.service';
import { Employee } from '../../../models/employee';
import { EmployeeService } from '../../../services/employee.service';
import { Tenant } from '../../../models/tenant';

@Component({
  selector: 'app-doc-drawer',
  standalone: false,
  templateUrl: './doc-drawer.component.html',
  styleUrl: './doc-drawer.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class DocDrawerComponent implements OnInit, OnDestroy {
  @Input() data: { employee?: Employee, medicalRecord?: MedicalRecord, patient?: any, tenant?: Tenant };
  
  private destroy$: Subject<boolean> = new Subject<boolean>();
  public form: FormGroup;
  public loading: boolean;

  get f(): { [index: string]: any } {
    return this.form.controls;
  }

  constructor(
    private formBuilder: FormBuilder,
    private auxiliarService: AuxiliarService,
    private patientService: PatientService,
    private medicalRecordService: MedicalRecordService,
    private translate: TranslateService,
    private logger: NGXLogger,
    private employeeService: EmployeeService,
  ){}

  ngOnInit(): void {
    this.loadForm();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  private loadForm(): void {
    this.form = this.formBuilder.group({
      description: ['', []],
      document: ['', [Validators.required], [extensionValidator(['pdf'])]], // [value, [sync validator], [async validator]]
      name: ['', [Validators.required]],
    });
  }

  public save(): void {
    this.form.markAllAsTouched();
    if (this.form.valid) {
      this.loading = true;
      const formData = _.cloneDeep(this.form.getRawValue());
      _.set(formData, 'document', _.first(_.get(formData, 'document')));
      if(this.data.medicalRecord && this.data.patient) {
        this.medicalRecordService.uploadMedicalRecordDoc(this.data.patient, this.data.medicalRecord, formData).pipe(
          finalize(() => this.loading = false),
          takeUntil(this.destroy$)
        ).subscribe({
          next: (res: any) => {
            this.logger.debug('[medicalRecordService] uploadMedicalRecordDoc res: ', res);
            this.auxiliarService.toast(this.translate.instant('success.doc-upload'), 'success');
            this.auxiliarService.closeDrawer('right', _.get(res, 'body.data'));
          },
          error: (err: any) => {
            this.logger.error('[medicalRecordService] uploadMedicalRecordDoc err:', err);
            this.auxiliarService.toast(this.translate.instant('error.doc-upload'), 'error');
          }
        });
      } else if(this.data.patient) {
        this.patientService.uploadPatientDoc(this.data.patient, formData).pipe(
          finalize(() => this.loading = false),
          takeUntil(this.destroy$)
        ).subscribe({
          next: (res: any) => {
            this.logger.debug('[patientService] uploadPatientDoc res: ', res);
            this.auxiliarService.toast(this.translate.instant('success.doc-upload'), 'success');
            this.auxiliarService.closeDrawer('right', _.get(res, 'body.data'));
          },
          error: (err: any) => {
            this.logger.error('[patientService] uploadPatientDoc err:', err);
            this.auxiliarService.toast(this.translate.instant('error.doc-upload'), 'error');
          }
        });
      } else if(this.data.employee) {
        this.employeeService.uploadEmployeeDoc(this.data.employee, formData, this.data.tenant).pipe(
          finalize(() => this.loading = false),
          takeUntil(this.destroy$)
        ).subscribe({
          next: (res: any) => {
            this.logger.debug('[employeeService] uploadEmployeeDoc res: ', res);
            this.auxiliarService.toast(this.translate.instant('success.doc-upload'), 'success');
            this.auxiliarService.closeDrawer('right', _.get(res, 'body.data'));
          },
          error: (err: any) => {
            this.logger.error('[employeeService] uploadEmployeeDoc err:', err);
            this.auxiliarService.toast(this.translate.instant('error.doc-upload'), 'error');
          }
        });
      }
    }
  }

  public cancel(): void {
    this.auxiliarService.closeDrawer('right');
  }
}
